import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { apiError, useAuth } from "@/context/AuthContext";
import { useNotifications } from "@/context/NotificationContext";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Bell, Check, X, ShieldCheck } from "@phosphor-icons/react";

export default function AccessRequests() {
  const { user } = useAuth();
  const { refresh } = useNotifications();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [selected, setSelected] = useState([]);
  const [grantPrompt, setGrantPrompt] = useState(null); // { mode: 'single'|'selected'|'all', target? }
  const [note, setNote] = useState("");

  const load = () => {
    setLoading(true);
    api.get("/access-requests", { params: { status: "pending" } }).then((r) => { setItems(r.data); setSelected([]); })
      .catch(() => toast.error("Could not load access requests"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const deny = async (r) => {
    setBusy(r.id);
    try {
      await api.post(`/access-requests/${r.id}/deny`);
      toast.success("Request denied");
      setItems((p) => p.filter((x) => x.id !== r.id));
      setSelected((p) => p.filter((id) => id !== r.id));
      refresh();
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Could not update request"); }
    finally { setBusy(null); }
  };

  const toggleSelect = (id) => setSelected((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const toggleSelectAll = () => setSelected((p) => (p.length === items.length ? [] : items.map((r) => r.id)));

  const openGrantPrompt = (mode, target = null) => { setGrantPrompt({ mode, target }); setNote(""); };

  const confirmGrant = async () => {
    if (!grantPrompt) return;
    const { mode, target } = grantPrompt;
    const trimmedNote = note.trim() || undefined;
    if (mode === "single") {
      setBusy(target.id);
      try {
        await api.post(`/access-requests/${target.id}/grant`, { note: trimmedNote });
        toast.success(`Access granted to ${target.requester_name}`);
        setItems((p) => p.filter((x) => x.id !== target.id));
        setSelected((p) => p.filter((id) => id !== target.id));
        refresh();
      } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Could not update request"); }
      finally { setBusy(null); setGrantPrompt(null); }
    } else {
      setBulkBusy(true);
      try {
        const body = mode === "all" ? { note: trimmedNote } : { ids: selected, note: trimmedNote };
        const { data } = await api.post("/access-requests/bulk-grant", body);
        toast.success(`Granted ${data.granted_count} request${data.granted_count === 1 ? "" : "s"}${data.skipped_count ? `, skipped ${data.skipped_count}` : ""}`);
        load();
        refresh();
      } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Could not grant requests"); }
      finally { setBulkBusy(false); setGrantPrompt(null); }
    }
  };

  return (
    <div className="max-w-4xl" data-testid="access-requests-page">
      <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-zinc-100 flex items-center gap-2"><Bell size={24} className="text-emerald-600" /> Access Requests</h1>
          <p className="text-sm text-zinc-500 mt-1">
            {isAdmin ? "Team members waiting for access to a restricted New Order document." : "Your requests for access to restricted documents."}
          </p>
        </div>
        {isAdmin && items.length > 0 && (
          <div className="flex items-center gap-2">
            <Button
              size="sm" variant="outline" disabled={selected.length === 0 || bulkBusy} onClick={() => openGrantPrompt("selected")}
              className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-sm gap-1" data-testid="grant-selected-btn"
            >
              <ShieldCheck size={14} /> Grant Selected{selected.length > 0 ? ` (${selected.length})` : ""}
            </Button>
            <Button
              size="sm" disabled={bulkBusy} onClick={() => openGrantPrompt("all")}
              className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm gap-1" data-testid="grant-all-btn"
            >
              <Check size={14} /> Grant All ({items.length})
            </Button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="text-sm text-zinc-500 py-8 text-center">Loading…</div>
      ) : items.length === 0 ? (
        <div className="border border-zinc-800 bg-zinc-900/30 rounded-sm py-12 text-center text-zinc-500 text-sm" data-testid="access-requests-empty">
          {isAdmin ? "No pending access requests right now." : "You have no pending access requests."}
        </div>
      ) : (
        <div className="space-y-2">
          {isAdmin && (
            <label className="flex items-center gap-2 px-4 py-1 text-xs text-zinc-500 cursor-pointer select-none" data-testid="select-all-requests">
              <Checkbox checked={selected.length === items.length} onCheckedChange={toggleSelectAll} className="border-zinc-600" />
              Select all
            </label>
          )}
          {items.map((r) => (
            <div key={r.id} className="border border-zinc-800 bg-zinc-900/30 rounded-sm px-4 py-3 flex items-center justify-between gap-4 flex-wrap" data-testid={`access-request-${r.id}`}>
              <div className="flex items-center gap-3 min-w-0">
                {isAdmin && (
                  <Checkbox
                    checked={selected.includes(r.id)} onCheckedChange={() => toggleSelect(r.id)}
                    className="border-zinc-600 shrink-0" data-testid={`select-request-${r.id}`}
                  />
                )}
                <div className="min-w-0">
                  <div className="text-sm text-zinc-100 font-semibold">{isAdmin ? r.requester_name : "You"}</div>
                  <div className="text-xs text-zinc-400 mt-0.5">
                    requested <span className="text-zinc-200">"{r.attachment_name}"</span> on{" "}
                    {r.order_no ? (
                      <Link to={`/orders/${r.order_id}/costing`} className="text-emerald-400 hover:underline">{r.order_no}{r.order_name ? ` (${r.order_name})` : ""}</Link>
                    ) : "an order that no longer exists"}
                  </div>
                  {r.created_at && <div className="text-[10px] text-zinc-600 mt-1">{new Date(r.created_at).toLocaleString()}</div>}
                </div>
              </div>
              {isAdmin ? (
                <div className="flex items-center gap-2 shrink-0">
                  <Button size="sm" disabled={busy === r.id} onClick={() => openGrantPrompt("single", r)} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm gap-1" data-testid={`grant-access-${r.id}`}>
                    <Check size={14} /> Grant
                  </Button>
                  <Button size="sm" variant="outline" disabled={busy === r.id} onClick={() => deny(r)} className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-sm gap-1" data-testid={`deny-access-${r.id}`}>
                    <X size={14} /> Deny
                  </Button>
                </div>
              ) : (
                <span className="text-xs text-amber-400 font-semibold shrink-0" data-testid={`request-status-${r.id}`}>Pending — waiting on admin</span>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!grantPrompt} onOpenChange={(v) => !v && setGrantPrompt(null)}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-md" data-testid="grant-note-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ShieldCheck size={18} className="text-emerald-500" /> Grant Access</DialogTitle>
            <DialogDescription className="text-zinc-500">
              {grantPrompt?.mode === "single" && `Grant "${grantPrompt.target?.attachment_name}" to ${grantPrompt.target?.requester_name}?`}
              {grantPrompt?.mode === "selected" && `Grant access to ${selected.length} selected request${selected.length === 1 ? "" : "s"}?`}
              {grantPrompt?.mode === "all" && `Grant access to all ${items.length} pending request${items.length === 1 ? "" : "s"}?`}
            </DialogDescription>
          </DialogHeader>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1.5 block">Note to requester (optional)</label>
            <Textarea
              value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Granted for review purposes only"
              className="bg-zinc-900 border-zinc-800 rounded-sm text-zinc-100 min-h-[70px]" data-testid="grant-note-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGrantPrompt(null)} className="border-zinc-700 rounded-sm" data-testid="grant-note-cancel">Cancel</Button>
            <Button
              onClick={confirmGrant} disabled={busy !== null || bulkBusy}
              className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm" data-testid="grant-note-confirm"
            >
              {busy !== null || bulkBusy ? "Granting…" : "Confirm Grant"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
